/**
 * One Box score category (ISSUE-990, Scores UX spec v1.0 section 7.5): a
 * card with no separate header, the category's name standing in the first
 * header cell in `bodyStrong`. The player column is sticky and capped at half
 * the card's width while the stat columns scroll sideways inside the card, so
 * the page itself never scrolls sideways at phone width (R3).
 *
 * Rows are 44 tall with a hairline under each one except the totals. Header
 * labels are `small` 600 `textTertiary`, cells `body`, centered, all tabular.
 * The totals row is `bodyStrong`, with the server's own sums, and is left out
 * when the server sends none (the team page's season tables, ISSUE-1384).
 * Every cell is the server's text; this component formats nothing.
 *
 * A row whose player the server names is one link into the player page (R5),
 * the whole row its target, with a 16px chevron in `textTertiary` at the end
 * of the name cell. A fade on the right edge marks stat columns still off
 * screen and leaves once the fan scrolls to the end (ISSUE-1166).
 */
import { cn, FOCUS_RING } from "@sportsmet/ui";
import { Link } from "@tanstack/react-router";
import { type ReactNode, useRef } from "react";

import { ChevronRightIcon } from "../../icons/glyphs";
import { PlayerHeadshot } from "../playerProfile/PlayerHeadshot";
import {
  BOX_SCORE_TOTALS_LABEL,
  boxScoreCellText,
  buildPlayerProfileSearch,
  type typeBoxScoreTable,
  type typeBoxScoreTeamInfo,
} from "./boxScoreView";
import { GAME_SECTION_CARD_CLASS } from "./GameSectionCard";
import { useIsScrollEndClipped } from "./scrollEndCue";

/** The sticky name column: never wider than half the card. */
const PLAYER_COLUMN_CLASS =
  "sticky left-0 z-10 w-40 max-w-1/2 md:w-56 pr-2 pl-3 text-left";
const ROW_RULE_CLASS = "border-b border-hairline";
const STAT_CELL_CLASS =
  "h-touch-target min-w-12 px-2 text-center tabular-nums whitespace-nowrap";

export const BoxScoreTable = ({
  table,
  team,
}: {
  table: typeBoxScoreTable;
  team: typeBoxScoreTeamInfo;
}): ReactNode => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const isEndClipped = useIsScrollEndClipped(scrollRef);
  return (
    <section
      data-testid="box-score-table"
      data-table-key={table.key}
      data-game-section-card=""
      className={cn(GAME_SECTION_CARD_CLASS, "gap-0")}
    >
      <div className="relative">
        <div
          ref={scrollRef}
          data-testid="box-score-table-scroll"
          className="overflow-x-auto"
        >
          <table className="w-full border-separate border-spacing-0 text-body">
            <caption className="sr-only">{`${team.name} ${table.label}`}</caption>
            <thead>
              <tr>
                <th
                  scope="col"
                  data-testid="box-score-player-header"
                  className={cn(
                    PLAYER_COLUMN_CLASS,
                    ROW_RULE_CLASS,
                    "h-touch-target bg-surface text-body-strong text-ink",
                  )}
                >
                  {table.label}
                </th>
                {table.columns.map((column) => (
                  <th
                    key={column.key}
                    scope="col"
                    className={cn(
                      STAT_CELL_CLASS,
                      ROW_RULE_CLASS,
                      "bg-surface text-small font-semibold text-ink-tertiary",
                    )}
                  >
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {table.rows.map((row, index) => {
                const isLinked = row.playerId !== "";
                // With no totals row under it, the last row sits on the
                // card's own edge and takes no rule of its own.
                const rowRule =
                  table.totals.length === 0 && index === table.rows.length - 1
                    ? ""
                    : ROW_RULE_CLASS;
                const nameCell = (
                  <>
                    <PlayerHeadshot
                      headshotUrl={row.headshotUrl ?? null}
                      size="sm"
                    />
                    <span className="flex min-w-0 flex-1 flex-col">
                      <span
                        data-testid="box-score-player-name"
                        className="truncate md:overflow-visible md:whitespace-normal md:wrap-anywhere"
                      >
                        {row.name}
                      </span>
                      {typeof row.position === "string" &&
                      row.position !== "" ? (
                        <span className="text-small text-ink-tertiary">
                          {row.position}
                        </span>
                      ) : null}
                    </span>
                    {isLinked ? (
                      <ChevronRightIcon
                        className="text-ink-tertiary"
                        testId="box-score-row-chevron"
                      />
                    ) : null}
                  </>
                );
                return (
                  <tr
                    key={`${row.playerId}-${String(index)}`}
                    className="relative"
                    data-testid="box-score-row"
                  >
                    <th
                      scope="row"
                      data-testid="box-score-player-cell"
                      className={cn(
                        PLAYER_COLUMN_CLASS,
                        rowRule,
                        "h-touch-target bg-surface font-normal",
                      )}
                    >
                      {isLinked ? (
                        <Link
                          to="/scores/player"
                          search={buildPlayerProfileSearch({ row, team })}
                          className={`flex min-h-touch-target items-center gap-2 text-ink no-underline after:absolute after:inset-0 after:content-[''] ${FOCUS_RING}`}
                        >
                          {nameCell}
                        </Link>
                      ) : (
                        <span className="flex min-h-touch-target items-center gap-2 text-ink">
                          {nameCell}
                        </span>
                      )}
                    </th>
                    {table.columns.map((column) => (
                      <td
                        key={column.key}
                        className={cn(
                          STAT_CELL_CLASS,
                          rowRule,
                          "bg-surface text-ink",
                        )}
                      >
                        {boxScoreCellText(row.cells, column.key)}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
            {table.totals.length > 0 ? (
              <tfoot>
                <tr data-testid="box-score-totals-row">
                  <th
                    scope="row"
                    className={cn(
                      PLAYER_COLUMN_CLASS,
                      "h-touch-target bg-surface text-body-strong text-ink",
                    )}
                  >
                    {BOX_SCORE_TOTALS_LABEL}
                  </th>
                  {table.columns.map((column) => (
                    <td
                      key={column.key}
                      className={cn(
                        STAT_CELL_CLASS,
                        "bg-surface text-body-strong text-ink",
                      )}
                    >
                      {boxScoreCellText(table.totals, column.key)}
                    </td>
                  ))}
                </tr>
              </tfoot>
            ) : null}
          </table>
        </div>
        {isEndClipped ? (
          <div
            aria-hidden="true"
            data-testid="box-score-scroll-cue-end"
            className="pointer-events-none absolute inset-y-0 right-0 z-20 w-8 bg-linear-to-l from-surface to-transparent"
          />
        ) : null}
      </div>
    </section>
  );
};
